import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  Lock,
  Plus,
  Shield,
  Sparkles,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe } from '@/pages/team/hooks/useTeamQueries';
import {
  PRESET_CODES,
  PRESET_LABELS,
  type PresetCode,
  type RoleDetail,
  type RoleSummary,
} from '@/pages/company/roles/rolesLogic';
import { useRoleDetail, useRoles } from '@/pages/company/roles/hooks/useRolesQueries';
import { RoleCard } from './components/RoleCard';
import { RoleEditorDrawer, type EditorMode } from './components/RoleEditorDrawer';
import { CreateFromPresetDrawer } from './components/CreateFromPresetDrawer';
import { SystemRoleDrawer } from './components/SystemRoleDrawer';
import { DeleteRoleDialog } from './components/DeleteRoleDialog';

function SectionLabel({
  children,
  count,
  sub,
}: {
  children: string;
  count: number;
  sub?: string;
}) {
  return (
    <div className="mb-2 mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <span>{children}</span>
      <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
        {count}
      </Badge>
      {sub && <span className="font-normal normal-case tracking-normal">· {sub}</span>}
    </div>
  );
}

/** Дропдаун «Создать из пресета ▾». */
function PresetMenu({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (preset: PresetCode) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          Создать из пресета
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Из пресета</DropdownMenuLabel>
        {PRESET_CODES.map((code) => (
          <DropdownMenuItem key={code} onSelect={() => onPick(code)}>
            <span>{PRESET_LABELS[code].name}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {PRESET_LABELS[code].hint}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * RolesPanel — вкладка «Роли и доступы» хаба /company (AK-343, ADR-040).
 *
 * Список системных (read-only) + кастомных ролей тенанта. Создание с нуля /
 * из пресета / дублирование системной. Правка кастомной с диф-подтверждением.
 * Удаление с guard'ом «есть участники». Гейт — company_admin (бэк enforce'ит
 * 403; UI прячет кнопки для не-админов).
 */
export function RolesPanel() {
  const me = useMe();
  const rolesQ = useRoles();

  const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetInitial, setPresetInitial] = useState<PresetCode>('kolkhoz');
  const [systemRoleId, setSystemRoleId] = useState<string | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleSummary | null>(null);

  // Для дублирования системной роли нужно её детально загрузить.
  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null);
  const duplicateSourceQ = useRoleDetail(duplicateSourceId);

  // Гейт: company_admin в активном тенанте. Бэк — источник истины (403);
  // UI прячет CTA заранее.
  const isAdmin = useMemo(() => {
    const m = me.data;
    if (!m) return true; // до загрузки не прячем — избегаем мигания
    const activeId = m.active_tenant?.id;
    const membership = m.memberships?.find((x) => x.tenant.id === activeId);
    return membership?.role === 'company_admin' || m.user.platform_role != null;
  }, [me.data]);

  const system = rolesQ.data?.system_roles ?? [];
  const custom = rolesQ.data?.custom_roles ?? [];

  const isForbidden = (rolesQ.error as { status?: number } | null)?.status === 403;
  const isLoading = rolesQ.isLoading || me.isLoading;

  const openEdit = (id: string) => setEditorMode({ kind: 'edit', roleId: id });
  const openCreate = () => setEditorMode({ kind: 'create' });
  const openPreset = (preset: PresetCode) => {
    setPresetInitial(preset);
    setPresetOpen(true);
  };

  // Системная роль → «Дублировать»: грузим detail, потом открываем редактор-копию.
  const startDuplicate = (id: string) => {
    setSystemRoleId(null);
    setDuplicateSourceId(id);
  };
  // Как только detail загрузился — открываем редактор в режиме копии.
  useEffect(() => {
    if (
      duplicateSourceId &&
      duplicateSourceQ.data &&
      duplicateSourceQ.data.id === duplicateSourceId
    ) {
      setEditorMode({ kind: 'duplicate-custom', from: duplicateSourceQ.data });
      setDuplicateSourceId(null);
    }
  }, [duplicateSourceId, duplicateSourceQ.data]);

  const onPresetCreated = (created: RoleDetail) => {
    // Открыть созданную роль в редакторе для правки предзаполненной матрицы.
    setEditorMode({ kind: 'edit', roleId: created.id });
  };

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Роли и доступы</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Что каждая роль может делать с разделами системы. Настраивается под ваше
            хозяйство.
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <PresetMenu onPick={openPreset} />
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Создать роль
            </Button>
          </div>
        )}
      </div>

      {isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Управление ролями доступно только администратору компании.
          </AlertDescription>
        </Alert>
      )}

      {rolesQ.error && !isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Не удалось загрузить роли. Обновите страницу.</AlertDescription>
        </Alert>
      )}

      {isLoading && !rolesQ.error && (
        <div className="mt-6 space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {!isLoading && !rolesQ.error && (
        <>
          <SectionLabel count={system.length} sub="нередактируемые">
            Системные роли
          </SectionLabel>
          <div className="space-y-2.5">
            {system.map((r) => (
              <RoleCard
                key={r.id}
                role={r}
                onOpen={() => setSystemRoleId(r.id)}
                onDuplicate={() => startDuplicate(r.id)}
              />
            ))}
          </div>

          <SectionLabel count={custom.length}>Роли вашего хозяйства</SectionLabel>
          {custom.length === 0 ? (
            <Card className="mt-1 px-6 py-12 text-center">
              <div className="mx-auto flex max-w-md flex-col items-center">
                <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
                  <Shield className="h-7 w-7" />
                </span>
                <h3 className="text-lg font-semibold text-foreground">Своих ролей пока нет</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Системные роли работают из коробки. Если стандартных не хватает — создайте
                  свою: с чистого листа или на основе пресета.
                </p>
                {isAdmin && (
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    <PresetMenu onPick={openPreset} />
                    <Button variant="accent-brand" onClick={openCreate}>
                      <Sparkles className="h-4 w-4" />
                      Создать роль
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <div className="space-y-2.5">
              {custom.map((r) => (
                <RoleCard
                  key={r.id}
                  role={r}
                  editable={isAdmin}
                  onOpen={() => openEdit(r.id)}
                  onEdit={() => openEdit(r.id)}
                  onDelete={() => setDeleteRole(r)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <RoleEditorDrawer
        mode={editorMode}
        open={editorMode !== null}
        onOpenChange={(o) => !o && setEditorMode(null)}
        customRoles={custom}
      />
      <CreateFromPresetDrawer
        open={presetOpen}
        onOpenChange={setPresetOpen}
        initialPreset={presetInitial}
        customRoles={custom}
        onCreated={onPresetCreated}
      />
      <SystemRoleDrawer
        roleId={systemRoleId}
        open={systemRoleId !== null}
        onOpenChange={(o) => !o && setSystemRoleId(null)}
        onDuplicate={startDuplicate}
      />
      <DeleteRoleDialog
        role={deleteRole}
        open={deleteRole !== null}
        onOpenChange={(o) => !o && setDeleteRole(null)}
      />
    </>
  );
}

export default RolesPanel;
